import { type JSX } from "react";
import { OfferEditableWrap } from "@/components/ClientOfferView";
import { OFFER_VALIDITY_DAYS } from "@/lib/offerDocumentDefaults";
import styles from "@/components/ClientOfferView.module.css";
import type { OfferSectionProps } from "@/components/offer/offerDocumentTypes";
import {
  buildVatLabel,
  fmtPln,
  formatPlDate,
  payableLines,
} from "@/components/offer/offerDocumentUtils";
import { estimateVatSummary } from "@/utils/mvpEstimateMath";

export function PriceSummarySection({ offerData }: OfferSectionProps): JSX.Element {
  const { lines, offerDate, offerTexts, onOfferTextsChange } = offerData;
  const visibleLines = payableLines(lines);
  const vat = estimateVatSummary(visibleLines);
  const vatLabel = buildVatLabel(visibleLines.map((line) => line.vatRate ?? 0));
  const validUntil = new Date(offerDate);
  validUntil.setDate(validUntil.getDate() + OFFER_VALIDITY_DAYS);
  const validUntilStr = formatPlDate(validUntil);
  const priceDisclaimerText =
    offerTexts?.priceDisclaimer?.trim() || "Ceny obowiązują w dniu wystawienia oferty.";

  return (
    <section className={styles.totals} aria-labelledby="client-offer-total-label">
      <div className={styles.totalsNote}>
        <strong id="client-offer-total-label">Podsumowanie cenowe · ważność do {validUntilStr}</strong>
        {onOfferTextsChange ? (
          <OfferEditableWrap enabled>
            <strong
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                onOfferTextsChange({
                  priceDisclaimer: e.currentTarget.textContent?.trim() ?? "",
                })
              }
            >
              {priceDisclaimerText}
            </strong>
          </OfferEditableWrap>
        ) : (
          <strong>{priceDisclaimerText}</strong>
        )}
      </div>
      <div className={styles.totalsCard}>
        <div className={styles.totalsRow}>
          <div className={styles.totalsK}>Suma netto</div>
          <div className={styles.totalsV}>{fmtPln(vat.netTotal)} zł</div>
        </div>
        <div className={styles.totalsRow}>
          <div className={styles.totalsK}>{vatLabel}</div>
          <div className={styles.totalsV}>{fmtPln(vat.vatAmount)} zł</div>
        </div>
        <div className={styles.totalsGrand}>
          <div className={styles.totalsGrandK}>Do zapłaty</div>
          <div className={styles.totalsGrandV}>
            {fmtPln(vat.grossTotal)} <small>PLN</small>
          </div>
        </div>
      </div>
    </section>
  );
}
